<template>
  <div class="full-width">
    <div class="multi-card-gradient">
      <GiveRewards
        :pendingPayment="display.pendingPayment"
        :showAddFunds="
          !loading.budgets && !!destinationBudget && hasBudgetFundingOptions(destinationBudget)
        "
        v-if="isRecognitionsEnabled() && showGiveRewards"
        @add-funds="fundBudget(null, accountBalance, null, false, null, null)"
      />
      <!-- [175387] 
       <RedeemRewards v-if="isOne10PointsEnabled()" @open-transfer-points="onOpenTransferPoints" /> 
      -->
      <RedeemRewards v-if="isOne10PointsEnabled()" />
      <v-card
        v-if="isPaymentCardEnabled()"
        color="systemPrimary"
        class="d-flex flex-column align-start pa-4 rounded-lg mb-4"
        id="whistle-wallet"
        width="100%"
        flat
      >
        <div class="d-flex justify-space-between align-center full-width mb-4">
          <Typography variant="h-5" as="h5" class="work-break text-left white--text">
            {{ cardName }}
          </Typography>
          <v-progress-circular
            v-if="loadingBalance"
            indeterminate
            color="white"
            width="2"
            size="25"
            class="ml-2"
          ></v-progress-circular>
          <Typography
            variant="h-5"
            as="h5"
            v-else
            class="word-break available to-spend white--text"
          >
            <strong>{{ computedAvailableToSpend }} </strong>
          </Typography>
        </div>
        <transition-group name="list-slide" class="full-width" tag="div">
          <v-tooltip top :disabled="!disableVirtualCardTile || showTaxInfoGate" :key="-3">
            <template v-slot:activator="{ on, attrs }">
              <div v-on="on" v-bind="attrs" class="">
                <WalletTileItem
                  :key="-1"
                  icon="mdi-credit-card"
                  color="brandPrimary"
                  :title="!showVirtualCardInfo && !isTest ? 'Get card' : 'View card'"
                  :disabled="disableVirtualCardTile && !showTaxInfoGate"
                  @click="toggleVirtualCardWidget"
                  class="list-slide-item"
                  :expandable="!showTaxInfoGate"
                  control-expansion
                  :expanded="showTaxInfoGate || display.virtualCard"
                >
                  <template #expanded-content>
                    <div
                      v-if="showTaxInfoGate"
                      class="tax-info-prompt mt-4 pa-6 rounded d-flex flex-column align-center text-center full-width"
                    >
                      <Typography variant="h-5" as="h3">
                        {{
                          showVirtualCardInfo
                            ? "Add tax info to view your card"
                            : "Add tax info to get your card"
                        }}
                      </Typography>
                      <Typography variant="body-1" as="p" class="mt-2 mb-0">
                        Takes about a minute.
                      </Typography>
                      <Button
                        color="brandPrimary"
                        class="mt-4"
                        :loading="taxInfoLoading"
                        @click.stop="openTaxInfoDrawer"
                      >
                        ADD TAX INFO
                      </Button>
                    </div>
                    <div class="full-width pt-4" v-else-if="display.virtualCard">
                      <div v-if="isTest">
                        <div class="py-2 px-1 d-flex flex-column align-center">
                          <v-img
                            src="@/assets/Wallet/whistle-card-default.png"
                            width="100%"
                            max-width="400"
                            max-height="200"
                            contain
                            class="mt-3 mb-1 list-slide-item"
                          />
                          <Typography variant="body-1" as="span" class="mt-1">
                            This card is for demo purposes only! <br /><a
                              @click.stop
                              href="https://whistlepayments.com/schedule-a-demo-payments/"
                              target="_blank"
                              >Request access</a
                            >
                            for a production account
                          </Typography>
                        </div>
                      </div>
                      <div class="list-slide-item virtual-card-slide" v-else>
                        <MarqetaVirtualCard
                          class="px-sm-2"
                          :cardOnly="true"
                          :elevation="false"
                          @get-cards="getCards"
                          @scrollToTop="scrollToTop"
                        />
                        <!-- This is here because for some reason, it existing caps the height of the card widget -->
                        <div class="hidden" style="max-height: 1px">111</div>
                      </div>
                    </div>
                  </template>
                </WalletTileItem>
              </div>
            </template>
            <span>Getting your card is unavailable at this time.</span>
          </v-tooltip>
          <WalletTileItem
            v-if="showCardActivationForm"
            :key="-4"
            icon="mdi-credit-card-check-outline"
            color="systemWalletGreen"
            iconRight="mdi-chevron-right"
            title="Activate your physical card"
            class="list-slide-item mt-4"
            @click="showCardActivationWidget"
          />
          <WalletTileItem
            v-if="showMyFunds"
            :key="-2"
            icon="mdi-wallet"
            color="systemWalletGreen"
            title="My funds"
            class="list-slide-item mt-4"
            :textRight="computedBalance"
            :loading="loadingBalance"
          />
          <WalletTileItem
            v-for="rule in sortedPaymentRules"
            :key="rule.paymentRuleId"
            :icon="rule.icon"
            :color="rule.expiring ? 'error' : 'systemWalletGreen'"
            :title="
              rule.classification == 'PER_DIEM' && !rule.description
                ? 'Per diem balance'
                : rule.nonCash
                ? rule.displayName
                : rule.description
            "
            :subtitle="rule.expiring ? 'Expires in ' + countdown(rule.endDate) : null"
            :subtitleTextClass="{
              'error--text': rule.expiringRedText,
            }"
            class="list-slide-item mt-4"
            :textRight="
              formatCurrency(rule.balance, !rule.nonCash, rule.unitSingular, rule.unitPlural)
            "
            expandable
          >
            <template #expanded-content>
              <div class="text-left pt-4" :key="`${rule.paymentRuleId}-expanded`">
                <div class="text-left pl-1">
                  <Typography v-if="rule.nonCash && rule.description" variant="body-1" as="p">
                    {{ rule.description }}
                  </Typography>
                  <Typography v-if="rule.nonCash" variant="body-1" as="p">
                    Note: This balance is non-monetary and cannot be spent using your card.
                  </Typography>
                  <Typography v-if="rule.mcc && rule.mcc.length > 0" variant="body-1" as="p">
                    Purchases limited to:
                    <Typography
                      v-for="(mcc, i) in rule.mcc"
                      :key="mcc.mccId"
                      variant="body-1"
                      as="span"
                      >{{ mcc.displayName + (i == rule.mcc.length - 1 ? "" : ", ") }}</Typography
                    >
                  </Typography>
                  <Typography v-else-if="!rule.nonCash" variant="body-1" as="p"
                    >No restrictions</Typography
                  >
                  <Typography v-if="!rule.nonCash" variant="body-1">
                    {{
                      !rule.expiring ? "No expiration" : "Expires on " + formatDate(rule.endDate)
                    }}
                  </Typography>
                </div>
              </div>
            </template>
          </WalletTileItem>
        </transition-group>
      </v-card>
    </div>
    <div
      class="d-flex flex-column align-start mt-5"
      :class="{ 'mx-7': !isMobile, 'mx-4': isMobile }"
    >
      <div
        v-if="!showTaxInfoGate && featureFlags['feature-company-merchandise']"
        class="d-flex mb-3 full-width justify-start align-center align-text-left cursor-pointer"
        @click="loadCompanyMerchandiseWidget"
      >
        <v-icon class="mr-2 pa-1">mdi-tshirt-crew-outline</v-icon>
        <strong class="word-break ml-1">Company merchandise</strong>
      </div>
      <div
        class="d-flex mb-3 full-width justify-start align-center align-text-left cursor-pointer"
        v-if="!showTaxInfoGate && showAppClipButton"
        @click="openAppClip"
      >
        <v-icon class="mr-2 pa-1 pl-2">mdi-cellphone-wireless</v-icon>
        <strong class="word-break ml-0">Add to Apple Pay</strong>
      </div>
      <div
        class="d-flex mb-3 full-width justify-start align-center align-text-left cursor-pointer"
        v-else-if="!showTaxInfoGate && showAndroidButton"
        @click="openInstantApp"
      >
        <v-icon class="mr-2 pa-1 pl-2">mdi-cellphone-wireless</v-icon>
        <strong class="word-break ml-0">Add to Google Wallet</strong>
      </div>
      <v-card
        v-else-if="
          !showTaxInfoGate && isPaymentCardEnabled() && showCardReplacementForm && !isModal
        "
        @click="showMobileWalletInstructions"
        flat
        class="mb-3"
        width="100%"
      >
        <div class="d-flex full-width align-center align-text-left">
          <v-icon class="mr-2 pa-1 pl-2">mdi-cellphone-wireless</v-icon>
          <strong class="word-break ml-0">Add to mobile wallet</strong>
        </div>
      </v-card>
      <div
        v-if="!showTaxInfoGate"
        class="d-flex full-width justify-start align-center align-text-left cursor-pointer"
        @click="togglePolicies"
      >
        <v-icon class="mr-2 pa-1">mdi-book-information-variant</v-icon>
        <strong class="word-break ml-1">Agreements & policies</strong>
        <v-spacer />
        <v-icon class="mr-2">{{ display.policies ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
      </div>
      <v-expand-transition>
        <div v-if="display.policies" class="mt-0 px-10">
          <v-card
            v-if="isPaymentCardEnabled()"
            flat
            class="mt-3"
            width="100%"
            @click="downloadFile(cardholderAgreementFilename)"
          >
            <div class="d-flex full-width align-center align-text-left">
              <v-progress-circular
                v-if="loading.cha"
                indeterminate
                color="brandPrimary"
                size="24"
                width="2"
                class="mr-3 ml-1 my-1"
              />
              <v-icon v-else class="mr-2 pa-1">mdi-file-document-outline</v-icon>
              <strong class="word-break ml-1">Cardholder Agreement</strong>
            </div>
          </v-card>
          <v-card
            v-if="isPaymentCardEnabled()"
            flat
            class="mt-3 full-width"
            @click="downloadFile(issuingBankPrivacyFilename)"
          >
            <div class="d-flex full-width align-center align-text-left">
              <v-progress-circular
                v-if="loading.bankPrivacyPolicy"
                indeterminate
                color="brandPrimary"
                size="24"
                width="2"
                class="mr-3 ml-1 my-1"
              />
              <v-icon v-else class="mr-2 pa-1">mdi-file-document-outline</v-icon>
              <strong class="word-break ml-1">Bank Privacy Policy</strong>
            </div>
          </v-card>
          <v-card
            v-if="isOne10PointsEnabled()"
            flat
            class="mt-3 full-width"
            @click="downloadFile(marketplaceTermsFilename)"
          >
            <div class="d-flex full-width align-center align-text-left">
              <v-progress-circular
                v-if="loading.marketplaceTermsAndConditions"
                indeterminate
                color="brandPrimary"
                size="24"
                width="2"
                class="mr-3 ml-1 my-1"
              />
              <v-icon v-else class="mr-2 pa-1">mdi-file-document-outline</v-icon>
              <strong class="word-break ml-1">Marketplace Agreement</strong>
            </div>
          </v-card>
          <v-card
            v-if="isPaymentCardEnabled()"
            flat
            class="mt-3 full-width"
            @click="downloadFile(faqFilename)"
          >
            <div class="d-flex full-width align-center align-text-left">
              <v-progress-circular
                v-if="loading.faq"
                indeterminate
                color="brandPrimary"
                size="24"
                width="2"
                class="mr-3 ml-1 my-1"
              />
              <v-icon v-else class="mr-2 pa-1">mdi-help-circle-outline</v-icon>
              <strong class="word-break ml-1">FAQ</strong>
            </div>
          </v-card>
        </div>
      </v-expand-transition>
      <div
        v-if="
          !showTaxInfoGate &&
          isPaymentCardEnabled() &&
          (showCardRequestForm ||
            showCardReplacementForm ||
            showUserEditForm ||
            showPINUpdateForm ||
            showCardPINForm ||
            showAppClipButton ||
            showAndroidButton)
        "
        class="d-flex full-width justify-start align-center align-text-left mt-3 cursor-pointer"
        @click="toggleAccountManagement"
      >
        <v-icon class="mr-2 pa-1">mdi-cog-outline</v-icon>
        <strong class="word-break ml-1">Account management</strong>
        <v-spacer />
        <v-icon class="mr-2">{{
          display.management ? "mdi-chevron-up" : "mdi-chevron-down"
        }}</v-icon>
      </div>
      <v-expand-transition>
        <div v-if="display.management" class="mt-0 px-10">
          <v-card
            v-if="showCardRequestForm"
            :disabled="!marqeta.canRegister"
            @click="showCardRequestWidget"
            flat
            class="mt-3"
            width="100%"
          >
            <div class="d-flex full-width align-center align-text-left">
              <v-icon class="mr-2 pa-1">mdi-credit-card-outline</v-icon>
              <strong class="word-break ml-1">{{
                showVirtualCardInfo ? "Request physical card" : "Request a card"
              }}</strong>
            </div>
          </v-card>
          <v-card
            v-if="showCardReplacementForm"
            :disabled="!marqeta.canRegister"
            @click="showCardReplacementWidget"
            flat
            class="mt-3"
            width="100%"
          >
            <div class="d-flex full-width align-center align-text-left">
              <v-icon class="mr-2 pa-1">mdi-credit-card-refresh-outline</v-icon>
              <strong class="word-break ml-1">Cancel or replace an existing card</strong>
            </div>
          </v-card>
          <v-card
            v-if="showUserEditForm"
            :disabled="!marqeta.canRegister"
            @click="showUserEditWidget"
            flat
            class="mt-3"
            width="100%"
          >
            <div class="d-flex full-width align-center align-text-left">
              <v-icon class="mr-2 pa-1">mdi-credit-card-settings-outline</v-icon>
              <strong class="word-break ml-1">Update your card billing info</strong>
            </div>
          </v-card>
          <v-card
            v-if="showPINUpdateForm || showCardPINForm"
            @click="showCardPINWidget"
            flat
            class="mt-3"
            width="100%"
          >
            <div class="d-flex full-width align-center align-text-left">
              <v-icon class="mr-2 pa-1">mdi-lock-outline</v-icon>
              <strong class="word-break ml-1">{{
                showPINUpdateForm ? "Update your PIN" : "Set your PIN"
              }}</strong>
            </div>
          </v-card>
        </div>
      </v-expand-transition>
      <div
        v-if="!showTaxInfoGate && isModal"
        class="d-flex full-width justify-start align-center align-text-left mt-3 cursor-pointer"
        @click="toggleRecentTransactions"
      >
        <v-icon class="mr-2 pa-1">mdi-format-list-bulleted</v-icon>
        <strong class="word-break ml-1">Recent transactions</strong>
      </div>
      <v-expand-transition>
        <div v-if="!showTaxInfoGate && display.transactions" class="mt-1 full-width text-left">
          <TransactionLog
            :key="1"
            :elevation="isModal ? 0 : 2"
            :isModal="isModal"
            @load-wallet="loadWallet"
          />
        </div>
      </v-expand-transition>
      <div
        v-if="magicLinkToken"
        class="d-flex full-width justify-start align-center align-text-left mt-3 cursor-pointer"
        @click="logout"
      >
        <v-icon class="mr-2 pa-1">mdi-logout</v-icon>
        <strong class="word-break ml-1">Logout</strong>
      </div>
      <CustomerServiceContact content="email" class="ml-2 mt-3" />
    </div>

    <div class="white-background">
      <v-dialog
        fullscreen
        v-model="dialogCardRequest"
        transition="dialog-bottom-transition"
        class="z-index-front"
        width="50%"
      >
        <v-card rounded="0">
          <MarqetaCardRequest
            v-if="dialogCardRequest"
            :cards="marqeta.cards"
            :alreadyRegistered="marqeta.alreadyRegistered"
            @reset="closeCardForm"
            @get-cards="getCards"
          ></MarqetaCardRequest>
        </v-card>
      </v-dialog>
    </div>
    <div class="white-background">
      <v-dialog
        fullscreen
        v-model="dialogCardReplacement"
        transition="dialog-bottom-transition"
        class="z-index-front"
      >
        <v-card rounded="0">
          <MarqetaCardReplacement
            v-if="dialogCardReplacement"
            :cards="marqeta.cards"
            :alreadyRegistered="marqeta.alreadyRegistered"
            @reset="closeCardForm"
            @get-cards="getCards"
          ></MarqetaCardReplacement>
        </v-card>
      </v-dialog>
    </div>
    <v-navigation-drawer
      v-model="display.funding"
      temporary
      fixed
      right
      width="500"
      disable-route-watcher
    >
      <StripePaymentMethodsManager
        v-if="display.funding"
        @reset="display.funding = false"
        @get-budgets="getBudgets"
        @get-funding-limit="getBudgetFundingStatuses"
        :destinationBudget="destinationBudget"
        :sourceBudget="null"
        :budgets="funding.budgets"
        :clientId="this.userProfile.clientId"
        :preloadMenu="null"
        :requestFunds="budgetFundingRequest"
        :fundingLimit="clientFundingStatus"
        source="BUDGETS"
        :displayBudgetTransfer="false"
      />
    </v-navigation-drawer>
    <!-- [175387]
    <v-navigation-drawer
      v-model="display.transferPointsToCard"
      temporary
      fixed
      right
      width="500"
      disable-route-watcher
    >
      <TransferPointsToCard
        @close="display.transferPointsToCard = false"
        :pointRatio="this.userProfile.Client.pointRatio"
        :minOne10PointsToCashTransferAmount="
          this.userProfile.Client.minOne10PointsToCashTransferAmount
        "
        :user-balance="funding.rewardsUserBalance"
        :card-name="cardName"
      />
    </v-navigation-drawer> 
    -->
    <TaxInfoFormDrawer
      :visible="display.taxInfoForm"
      :issue-card="canIssueCardOnTaxInfo"
      @update:visible="onTaxInfoDrawerVisibilityChange"
      @saved="onTaxInfoSaved"
    />
  </div>
</template>

<script>
// Packages
import moment from "moment";
import currency from "currency.js";
import { mapState } from "vuex";

// Services
import UserService from "@/services/UserService";
import BudgetService from "@/services/BudgetService";
import WalletService from "@/services/WalletService";

// Components
import MarqetaVirtualCard from "@/components/wallet/MarqetaVirtualCard.vue";
import MarqetaCardRequest from "@/components/wallet/MarqetaCardRequest.vue";
import MarqetaCardReplacement from "@/components/wallet/MarqetaCardReplacement.vue";
import TransactionLog from "@/components/wallet/TransactionLog.vue";
import StripePaymentMethodsManager from "@/components/stripe/StripePaymentMethodsManager.vue";
import Typography from "@/components/generic/Typography.vue";
import Button from "@/components/generic/Button.vue";
import CustomerServiceContact from "@/components/help/CustomerServiceContact";
import RedeemRewards from "@/components/wallet/rewards/RedeemRewards.vue";
import GiveRewards from "@/components/wallet/rewards/GiveRewards.vue";
import TaxInfoFormDrawer from "@/components/wallet/rewards/TaxInfoFormDrawer.vue";
import WalletTileItem from "@/components/wallet/WalletTileItem.vue";
// [175387] import TransferPointsToCard from "@/components/wallet/rewards/TransferPointsToCard.vue";

// Mixins
import isOne10PointsEnabled from "@/mixins/one10Points/isOne10PointsEnabled";
import isPaymentCardEnabled from "@/mixins/one10Points/isPaymentCardEnabled";
import isRecognitionsEnabled from "@/mixins/isRecognitionsEnabled";
import budgetFundingMixin from "@/mixins/budgetFundingMixin.js";
import PaymentAdminMixin from "@/mixins/paymentAdminMixin";
import cardProductMixin from "@/mixins/cardProduct";
import taxInfoGateMixin from "@/mixins/taxInfoGate";
import {
  CARDHOLDER_AGREEMENT_FILENAME,
  FAQ_FILENAME,
  ISSUING_BANK_PRIVACY_FILENAME,
  MARKETPLACE_TERMS_FILENAME,
} from "@/constants/legalDocuments";

export default {
  name: "MarqetaWidgetManagerV2",
  props: {
    isModal: {
      type: Boolean,
      default: false,
    },
    isolate: String,
    showVirtualCard: Boolean,
    previousRoute: String,
    showGiveRewards: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    // MarqetaBusinessCreation,
    MarqetaCardRequest,
    MarqetaCardReplacement,
    MarqetaVirtualCard,
    TransactionLog,
    StripePaymentMethodsManager,
    Typography,
    Button,
    CustomerServiceContact,
    RedeemRewards,
    GiveRewards,
    TaxInfoFormDrawer,
    WalletTileItem,
    // [175387] TransferPointsToCard,
  },
  data() {
    return {
      budgetService: null,
      dialogCardRequest: false,
      dialogCardReplacement: false,
      showManageCard: false,
      showPolicyInfo: false,
      taxInfoLoading: false,
      marketplaceTermsFilename: MARKETPLACE_TERMS_FILENAME,
      cardholderAgreementFilename: CARDHOLDER_AGREEMENT_FILENAME,
      issuingBankPrivacyFilename: ISSUING_BANK_PRIVACY_FILENAME,
      faqFilename: FAQ_FILENAME,
      loading: {
        cha: false,
        bankPrivacyPolicy: false,
        faq: false,
        balance: false,
        fundingStatus: false,
        budgets: false,
        marketplaceTermsAndConditions: false,
      },
      display: {
        virtualCard: false,
        policies: false,
        management: false,
        transactions: false,
        funding: false,
        pendingPayment: false,
        taxInfoForm: false,
        // [175387] transferPointsToCard: false,
      },
      // Set when the tax info drawer reports a successful save, so dismissing the
      // confirmation can refresh once more for records that arrive late.
      taxInfoSavedPendingRefresh: false,
      search: {
        authorizations: null,
        transactions: null,
      },
      funding: {
        budgets: [],
        cumulativeBalance: 0,
        sourceBudget: null,
        destinationBudget: null,
        preloadPaymentMenu: null,
        fundingArray: [],
        rewardsUserBalance: 0,
      },
      transactionColumns: [
        { text: "Date", value: "formattedDate", width: "35%", sortable: false },
        {
          text: "Description",
          value: "merchantName",
          width: "50%",
          sortable: false,
          align: "start",
        },
        {
          text: "Amount",
          value: "amount",
          width: "15%",
          align: "end",
          sortable: false,
        },
      ],
      pendingPaymentTimeout: null,
    };
  },
  created() {
    // this.budgetService = new BudgetService();
    this.budgetService = BudgetService;

    this.dialogCardRequest = false;
    this.refreshWallet();
    this.getBudgets();
    this.checkTaxInfoStatus();
    // If we're coming from the payment wizard, we check for pending payments to show the asterisk next to budget balance
    if (
      this.previousRoute &&
      (this.previousRoute == "paymentwizard" || this.previousRoute == "recognitionwizard")
    ) {
      this.getPendingPayments(1);
    }
  },
  beforeUnmount() {},
  unmounted() {
    clearInterval(this.pendingPaymentTimeout);
  },
  methods: {
    // [175387]
    // onOpenTransferPoints(balance) {
    //   this.funding.rewardsUserBalance = balance;
    //   this.display.transferPointsToCard = true;
    // },
    formatCurrency(amount, cash = true, unitSingular = "unit", unitPlural = "units") {
      return (
        currency(amount, { symbol: cash ? "$" : "" }).format() +
        (cash ? "" : ` ${amount == 1 ? unitSingular : unitPlural}`)
      );
    },
    logout() {
      this.$auth.logout({
        returnTo: window.location.origin,
      });
    },
    getCards(source) {
      console.log("Calling getCards", source);
      this.refreshWallet();
    },
    refreshWallet() {
      this.$store.dispatch("refreshWallet");
      window.scrollTo(0, 0);
    },
    openTaxInfoDrawer() {
      this.display.taxInfoForm = true;
    },
    onTaxInfoDrawerVisibilityChange(visible) {
      this.display.taxInfoForm = visible;

      // The "Virtual card issued" row is written by a Marqeta webhook, so it
      // usually lands after the refresh in onTaxInfoSaved has already run. The
      // user spends a few seconds on the confirmation screen, so refreshing again
      // when they dismiss it picks the row up without polling for it.
      if (!visible && this.taxInfoSavedPendingRefresh) {
        this.taxInfoSavedPendingRefresh = false;
        this.refreshWallet();
        this.broadcastTaxInfoRefresh();
      }
    },
    async onTaxInfoSaved(response) {
      // Card issuance is handled by the backend saveTaxInfo endpoint.
      // The drawer is deliberately left open here: on save it switches to its own
      // "Tax Info Submitted!" confirmation, and closing it from this handler would
      // flash that past the user. They dismiss it themselves via CLOSE, which
      // emits update:visible. The refreshes below run behind the open drawer.
      this.taxInfoSavedPendingRefresh = true;

      const cardIssuance = response?.result?.cardIssuance;

      if (cardIssuance === "failed") {
        // Marqeta genuinely errored — tax info is saved, so let them retry the card.
        this.$store.dispatch("showError", {
          title: "Card could not be issued",
          message: "Your tax info was saved. Please try requesting your card again.",
        });
      }

      // Load the newly issued card BEFORE clearing the gate. The store's
      // refreshWallet does not await setMarqeta, so dropping the gate first
      // leaves marqeta.cards stale and the tile renders "Get card" instead of
      // "View card".
      this.taxInfoLoading = true;
      try {
        await this.$store.dispatch("setMarqeta");
      } catch (err) {
        console.log("Error refreshing cards after saving tax info: ", err);
      } finally {
        this.taxInfoLoading = false;
      }

      this.clearTaxInfoGate();
      this.refreshWallet();

      // Issuance was skipped on purpose (e.g. a LEARNING client, who requests
      // cards manually). Nothing failed, so drop them into the same request
      // flow they would have seen before this feature existed.
      if (cardIssuance === "skipped" && !this.showVirtualCardInfo) {
        this.showCardRequestWidget();
      }
    },
    // Tax info captured in another part of the wallet - the redeem drawer - also
    // unblocks card issuance, and the backend issues the virtual card during that
    // save. Only this component loads cards, so without refreshing here the tile
    // keeps offering "Get card" for a card the user already has.
    async onTaxInfoCapturedElsewhere() {
      if (!this.isPaymentCardEnabled()) return;

      this.taxInfoLoading = true;
      try {
        await this.$store.dispatch("setMarqeta");
      } catch (err) {
        console.log("Error refreshing cards after tax info was saved elsewhere: ", err);
      } finally {
        this.taxInfoLoading = false;
      }
    },
    closeCardForm(loadVirtualCard) {
      console.log("Closing card form ", loadVirtualCard);
      this.dialogCardRequest = false;
      this.dialogCardReplacement = false;
      if (loadVirtualCard) {
        if (this.displayWalletMenu) {
          // The wallet menu is already open so we load the new wallet screen;
          this.walletScreen = "display-virtual";
          this.scrollToTopOfWalletDrawer();
        } else {
          this.$store.dispatch("setDisplayWalletMenu", true);
          // We have to set the source page so we know when they click back, it closes the drawer
          this.$store.dispatch("setSourceWalletPage", "wallet");
          this.walletScreen = "display-virtual";
          this.scrollToTopOfWalletDrawer();
        }
      }
    },
    async downloadFile(fileName) {
      if (fileName.includes("faq")) this.loading.faq = true;
      else if (fileName.includes("cardholder")) this.loading.cha = true;
      else if (fileName.includes("privacy")) this.loading.bankPrivacyPolicy = true;
      else if (fileName.includes("marketplace")) this.loading.marketplaceTermsAndConditions = true;
      await UserService.downloadCardProductFile(fileName, this.magicLinkToken);
      this.loading.faq = false;
      this.loading.cha = false;
      this.loading.bankPrivacyPolicy = false;
      this.loading.marketplaceTermsAndConditions = false;
    },
    showCardRequestWidget() {
      if (this.showTaxInfoGate) return;
      this.dialogCardRequest = true;
    },
    showCardReplacementWidget() {
      this.display.virtualCard = false;
      this.dialogCardReplacement = true;
    },

    showVirtualCardWidget() {
      // If we're on the wallet screen and the wallet drawer isn't open, then we open it
      if (this.onWalletPage && !this.displayWalletMenu) {
        this.$store.dispatch("setDisplayWalletMenu", true);
        // We have to set the source page so we know when they click back, it closes the drawer
        this.$store.dispatch("setSourceWalletPage", "wallet");
      }
      this.scrollToTopOfWalletDrawer();
      this.walletScreen = "display-virtual";
    },
    showCardActivationWidget() {
      // If we're on the wallet screen and the wallet drawer isn't open, then we open it
      if (this.onWalletPage && !this.displayWalletMenu) {
        this.$store.dispatch("setDisplayWalletMenu", true);
        // We have to set the source page so we know when they click back, it closes the drawer
        this.$store.dispatch("setSourceWalletPage", "wallet");
      }
      this.scrollToTopOfWalletDrawer();
      this.walletScreen = "card-activation";
    },
    showCardPINWidget() {
      // If we're on the wallet screen and the wallet drawer isn't open, then we open it
      if (this.onWalletPage && !this.displayWalletMenu) {
        this.$store.dispatch("setDisplayWalletMenu", true);
        // We have to set the source page so we know when they click back, it closes the drawer
        this.$store.dispatch("setSourceWalletPage", "wallet");
      }
      this.scrollToTopOfWalletDrawer();
      this.walletScreen = "card-pin";
    },
    showMobileWalletInstructions() {
      if (this.showVirtualCardInfo) {
        // OPen the wallet and scroll to the top
        this.toggleVirtualCardWidget(true);
        this.scrollToTopOfWalletDrawer();
      } else if (this.isTest) this.scrollToTop();
      // If we're on the wallet screen and the wallet drawer isn't open, then we open it
      if (this.onWalletPage && !this.displayWalletMenu) {
        this.$store.dispatch("setDisplayWalletMenu", true);
        // We have to set the source page so we know when they click back, it closes the drawer
        this.$store.dispatch("setSourceWalletPage", "wallet");
      }
      //this.scrollToTopOfWalletDrawer();
      //this.walletScreen = "display-mobile-wallet-instruction";
      this.$store.dispatch("setWalletMenuScreen", "display-add-to-mobile");
    },
    loadCompanyMerchandiseWidget() {
      window.open("https://whistle.axomo.com/", "_blank");
    },
    openAppClip() {
      // We need to change the current route and add the token to the url
      // let query = Object.assign({}, this.$route.query);
      // query.token = this.magicLinkToken || this.token;
      // if (this.magicLinkToken) query["magic-link"] = "1";
      // this.$router.replace({ query });
      //   // const url = process.env.VUE_APP_APP_CLIP_URL;

      // let url = `https://app.wewhistle.com/wallet/app-clip?token=${this
      //   .magicLinkToken || this.token}`;
      // let url = `http://localhost:8080/wallet/app-clip?token=${this
      // .magicLinkToken || this.token}`;
      // if (this.magicLinkToken) url += `&magic-link=1`;

      // setTimeout(() => {
      window.open(this.appClipUrl, "_blank");
      // console.log("Changing router window");
      // this.$router.push({
      //   name: "walletappclip",
      //   query: {
      //     token: this.magicLinkToken || this.token,
      //     "magic-link": this.magicLinkToken ? 1 : 0
      //   }
      // });
      // }, 2000);
    },
    openInstantApp() {
      window.open(this.instantAppUrl, "_blank");
    },
    showUserEditWidget() {
      this.scrollToTopOfWalletDrawer();
      this.walletScreen = "update-user";
      // If we're on the wallet screen and the wallet drawer isn't open, then we open it
      if (this.onWalletPage && !this.displayWalletMenu) {
        this.$store.dispatch("setDisplayWalletMenu", true);
        // We have to set the source page so we know when they click back, it closes the drawer
        this.$store.dispatch("setSourceWalletPage", "wallet");
      }
    },
    scrollToTopOfWalletDrawer() {
      this.$emit("scroll-top");
      // If we're on the wallet screen and the wallet drawer isn't open, then we open it
      if (this.onWalletPage && !this.displayWalletMenu) {
        this.$store.dispatch("setDisplayWalletMenu", true);
        // We have to set the source page so we know when they click back, it closes the drawer
        this.$store.dispatch("setSourceWalletPage", "wallet");
      }
    },
    loadWallet(tab) {
      // We have to check if we're already on the router. If so, we just close the profile
      if (this.onWalletPage) {
        // We close the wallet
        //this.closeWallet();
        this.$emit("close-wallet");
        console.log("emitting close-wallet");
      } else {
        this.$router.push({
          name: "wallet",
          params: { initialPageIndexToLoad: tab },
        });
      }
    },
    scrollToTop() {
      document.getElementsByClassName("v-dialog v-dialog--active")[0].scrollTop = 0;
    },
    loadPaymentConsole() {
      this.$router.push({
        name: "paymentwizard",
        query: { previousRoute: this.$route.name },
        params: {
          //   preload: "wizard",
          previousRoute: this.$route.name,
          //   oneTimePayment: true
        },
      });
    },
    loadProfile(tab) {
      if (this.$route.name != "profile")
        this.$router.push({
          name: "profile",
          params: { initialPageIndexToLoad: tab },
        });
    },
    countdownMillis(date) {
      var now = moment();
      var localTime = moment(date).utc();
      let dur = moment.duration(localTime - now);
      return dur;
    },
    countdown(date) {
      // console.log(date);
      var now = moment();
      var localTime = moment(date).utc();
      let dur = moment.duration(localTime - now);
      if (dur.years() != 0) {
        return dur.years() + (dur.years() == 1 ? " year" : " years");
      }
      if (dur.months() != 0) {
        return dur.months() + (dur.months() == 1 ? " month" : " months");
      }
      if (dur.days() != 0) {
        return dur.days() + (dur.days() == 1 ? " day" : " days");
      }
      // console.log(dur.hours());
      if (dur.hours() != 0) {
        return dur.hours() + (dur.hours() == 1 ? " hour" : " hours");
      }
      if (dur.minutes() != 0) {
        return dur.minutes() + (dur.minutes() == 1 ? " minute" : " minutes");
      }
    },
    formatDate(date) {
      var localTime = moment(date).utc().local().format("MMM Do, YYYY, h:mm a");
      return localTime;
    },

    // V2
    toggleVirtualCardWidget(dontHideCard) {
      if (this.showTaxInfoGate) return;
      if (dontHideCard === true && this.showVirtualCardInfo && this.display.virtualCard)
        return console.log("Returning early because we don't need to hide the virtual card");
      if (!this.showVirtualCardInfo && !this.isTest) this.showCardRequestWidget();
      else this.display.virtualCard = !this.display.virtualCard;
    },
    togglePolicies() {
      this.display.policies = !this.display.policies;
    },
    toggleAccountManagement() {
      this.display.management = !this.display.management;
    },
    toggleRecentTransactions() {
      this.display.transactions = !this.display.transactions;
    },
    async getBudgetFundingStatuses() {
      // this is used to get the client's balance on how much they have funded if they're using Whistle's KYB status
      // So we can cap them at 1,000
      try {
        this.loading.fundingStatus = true;
        const response = await this.budgetService.getBudgetFundingStatuses(this.magicLinkToken);
        console.log("KYB funding response ", response);
        this.loading.fundingStatus = false;

        this.funding.fundingArray = response.map((x) => {
          if (x.cooldownExpiration)
            x.cooldownExpirationLocal = moment(x.cooldownExpiration)
              .local()
              .format("MMM Do h:mm a");
          return x;
        });
      } catch (e) {
        console.log("Error getting funding statuses ", e);
      }
    },
    async getBudgets() {
      // this is used to get the client's balance on how much they have funded if they're using Whistle's KYB status
      // So we can cap them at 1,000
      if (!this.adminView) return console.log("No admin privileges. Not getting budgets");
      try {
        this.loading.budgets = true;
        const response = await this.budgetService.getBudgetsAdminView(
          {
            clientId: this.userProfile.clientId,
            lite: 1,
            awardId: 1,
            sum: 1,
          },
          this.magicLinkToken
        );
        console.log("Budget response ", response);
        this.loading.budgets = false;

        this.funding.budgets = response.result.rows;
        this.funding.cumulativeBalance = response.result.sum;
      } catch (e) {
        console.log("Error getting budgets ", e);
      }

      this.getBudgetFundingStatuses();
    },
    async getPendingPayments(attempt = 1) {
      try {
        // this.loading.recent = true;
        console.log("Getting pending payments for attempt ", attempt);
        const filter = `clientId == ${
          this.userProfile.clientId
        } && status = 'Draft' && createDate >= '${moment().subtract(15, "minute")}'`;
        console.log("Parent payment rule filter ", filter);
        let results = await WalletService.getParentPaymentRulesV2(
          {
            filter,
            limit: 1,
            sort: "createDate DESC",
            extract: "parentPaymentRuleId,clientId",
          },
          this.magicLinkToken
        );
        console.log("Got recent parent payment rules ", results.result.rows);
        if (results.result.count) this.display.pendingPayment = true;
        else this.display.pendingPayment = false;

        // We're on a repeat attempt and didn't find any, so we can refresh
        if (attempt > 1 && !results.result.count) this.getBudgets();
        else if (attempt < 3 && results.result.count) {
          this.pendingPaymentTimeout = setTimeout(
            () => this.getPendingPayments(attempt + 1),
            10000
          );
        }
      } catch (err) {
        console.log("Error with parent payment rules ", err);
      }
      //  finally {
      //   this.loading.recent = false;
      // }
    },
    fundBudget(
      source,
      destination,
      preloadMenu,
      requestFunds = false,
      amount = null,
      budgetActivityId = null
    ) {
      console.log(source);
      console.log(destination);
      console.log(preloadMenu);
      console.log(requestFunds);
      console.log(amount);
      this.display.funding = true;
      this.sourceBudgetForFunding = source;
      this.destinationBudgetForFunding = destination;
      this.preloadPaymentMenu = preloadMenu;
      this.budgetFundingRequest = requestFunds;
      this.preloadFundingAmount = amount;
      this.preloadBudgetActivityId = budgetActivityId;
    },
  },
  mixins: [
    PaymentAdminMixin,
    isOne10PointsEnabled,
    isPaymentCardEnabled,
    isRecognitionsEnabled,
    budgetFundingMixin,
    cardProductMixin,
    taxInfoGateMixin,
  ],
  computed: {
    ...mapState([
      "userProfile",
      "walletProfile",
      "permissions",
      "roles",
      "walletMenuScreen",
      "displayWalletMenu",
      "marqeta",
      "balances",
      "cashBalance",
      "loadingBalance",
      "magicLinkToken",
      "token",
      "featureFlags",
    ]),
    isMobile() {
      return this.$vuetify.breakpoint.xs || this.$vuetify.breakpoint.sm;
    },
    cardName() {
      return this.cardProductName();
    },
    isTest() {
      return process.env.VUE_APP_ENVIRONMENT == "test";
    },
    onWalletPage() {
      return this.$route.name == "wallet";
    },
    walletScreen: {
      get: function () {
        return this.walletMenuScreen;
      },
      set: function (newValue) {
        this.$store.dispatch("setWalletMenuScreen", newValue);
      },
    },
    // hasOtherAwards() {
    //   return (
    //     this.balances.filter(x => parseFloat(x.balance).toFixed(0) > 0).length >
    //     0
    //   );
    // },
    sortedPaymentRules() {
      return (this.walletProfile?.paymentRules || [])
        ?.filter((x) => x.displayBalanceInWallet && x.expiring)
        ?.sort((a, b) => this.countdownMillis(a.endDate) - this.countdownMillis(b.endDate))
        ?.concat(
          (this.walletProfile?.paymentRules || [])
            ?.filter((x) => x.displayBalanceInWallet && !x.expiring)
            ?.sort((a, b) => this.countdownMillis(b.startDate) - this.countdownMillis(a.startDate))
        )
        ?.concat(this.nonCashBalances);
    },
    nonCashBalances() {
      if (!this.balances || !Array.isArray(this.balances)) return [];
      return this.balances
        .filter((x) => x.balance > 0 && (this.isOne10PointsEnabled() ? !x.cashValuePoints : true))
        .map((x) => {
          console.log(x);
          x.icon = "mdi-gift";
          x.paymentRuleId = `noncash-${x.awardId}`;
          x.nonCash = true;
          return x;
        });
    },
    showCardRequestForm() {
      if (!this.isPaymentCardEnabled()) {
        return false;
      }
      // if (this.userProfile && this.userProfile.businessCountry != "US") {
      //   return false;
      // }
      if (
        !this.marqeta.canRegister /*||
        (this.isolate != "cardRequestForm" && this.isolate != null)*/
      ) {
        return false;
      }
      // console.log(this.marqeta);
      if (this.marqeta.error_code) {
        // We had an error, so we check the error code and render things differently
        if (this.marqeta.error_code == 1190004) {
          // The user isn't setup, so we allow any card requests and hide card activation
          return true;
        } else if (
          this.marqeta.error_code == 1190005 ||
          this.marqeta.error_code == 1190019 ||
          this.marqeta.error_code == 1190020
        ) {
          // We hide all because the business is not setup
          return false;
        }
      } else {
        // If the user doesn't have a card, then we show that request form
        if (!this.marqeta.cards.find((x) => x.type == "virtual")) {
          return true;
        } else if (
          !this.marqeta.cards.find((x) => x.type == "physical") &&
          this.cashBalance >= this.userProfile.Client.physicalCardFee
        ) {
          return true;
        } else {
          return false;
        }
      }

      return false;
    },
    showCardReplacementForm() {
      // if (this.userProfile.clientId == 1) {
      //   return true;
      // }
      if (
        !this.marqeta.canRegister /*||
        (this.isolate != "cardReplacementForm" && this.isolate != null)*/
      ) {
        return false;
      }
      if (this.marqeta.error_code) {
        return false;
      } else {
        // If the user has a card, then we show the replacement form
        if (
          this.marqeta.cards.find((x) => x.type == "physical") ||
          this.marqeta.cards.find((x) => x.type == "virtual")
        ) {
          return true;
        } else {
          return false;
        }
      }
    },
    showCardActivationForm() {
      // if (this.userProfile.clientId == 1) {
      //   return true;
      // }
      if (
        !this.marqeta.canRegister /*||
        (this.isolate != "cardActivationForm" && this.isolate != null)*/
      ) {
        return false;
      }
      if (this.marqeta.error_code) {
        // We had an error, so we check the error code and render things differently
        return false;
      } else {
        // If the user has a physical card but unactivated, then we show the activation form
        if (this.marqeta.cards.find((x) => x.type == "physical" && x.state == "UNACTIVATED")) {
          return true;
        } else {
          return false;
        }
      }
    },
    showCardPINForm() {
      // if (this.userProfile.clientId == 1) {
      //   return true;
      // }
      if (
        !this.marqeta.canRegister /*||
        (this.isolate != "cardPINForm" && this.isolate != null)*/
      ) {
        return false;
      }
      if (this.marqeta.error_code) {
        // We had an error, so we check the error code and render things differently
        return false;
      } else {
        // If the user has a physical card but unactivated, then we show the activation form
        if (this.marqeta.cards.find((x) => x.pin_is_set === false && x.state == "ACTIVE")) {
          return true;
        } else {
          return false;
        }
      }
    },
    showPINUpdateForm() {
      // if (this.userProfile.clientId == 1) {
      //   return true;
      // }
      if (
        !this.marqeta.canRegister /*||
        (this.isolate != "cardPINUpdateForm" && this.isolate != null)*/
      ) {
        return false;
      }
      if (this.marqeta.error_code) {
        // We had an error, so we check the error code and render things differently
        return false;
      } else {
        // If the user has a physical card but unactivated, then we show the activation form
        if (this.marqeta.cards.find((x) => x.pin_is_set === true && x.state == "ACTIVE")) {
          return true;
        } else {
          return false;
        }
      }
    },
    showBusinessCreationForm() {
      if (this.userProfile.clientId == 1) {
        return true;
      }
      if (this.marqeta.error_code) {
        // We had an error, so we check the error code and render things differently
        if (this.marqeta.error_code == 1190004) {
          // The user isn't setup, so we allow any card requests and hide card activation
          return false;
        } else if (this.marqeta.error_code == 1190005) {
          // We hide all because the business is not setup
          if (this.permissions.includes("marqeta:create:business")) {
            return true;
          }
        }
      } else {
        return false;
      }
      return false;
    },
    showVirtualCardInfo() {
      // if (this.userProfile && this.userProfile.businessCountry != "US") {
      //   return false;
      // }
      // if (!this.marqeta.canRegister) {
      //   return false;
      // }
      if (
        this.marqeta.error_code /*||
        (this.isolate != "virtualCardInfo" && this.isolate != null)*/
      ) {
        return false;
      } else {
        // If the user doesn't have a virtual card, then we show that request form
        if (!this.marqeta.cards.find((x) => x.type == "virtual")) {
          return false;
        } else {
          return true;
        }
      }
    },
    showUserEditForm() {
      // if (this.userProfile.clientId == 1) {
      //   return true;
      // }
      //  We base it on alreadyRegisterd
      if (!this.marqeta.canRegister) {
        return false;
      }
      return this.marqeta.alreadyRegistered;
    },
    showCardTokenization() {
      if (this.userProfile.clientId == 1) {
        return true;
      }
      if (this.marqeta.error_code) {
        // We had an error, so we check the error code and render things differently
        return false;
      } else {
        // If the user doesn't have a physical card, then we show that request form
        if (
          this.marqeta.cards.find((x) => x.type == "physical") ||
          this.marqeta.cards.find((x) => x.type == "virtual")
        ) {
          return true;
        } else {
          return false;
        }
      }
    },
    computedBalance: {
      cache: false,
      get: function () {
        //Subtraction can cause lots of floating point problems of 0.99999999 and toFixed rounds up, so I hack a solution here
        return this.formatCurrency(this.cashBalance || 0, true);
      },
    },

    computedAvailableToSpend: {
      cache: false,
      get: function () {
        var bigTotal = currency(0);

        var paymentRules =
          (this.walletProfile?.paymentRules || [])?.filter((x) => x.displayBalanceInWallet) || [];

        bigTotal = paymentRules.reduce((accumulator, currentValue) => {
          return accumulator.add(currentValue.balance);
        }, currency(0));

        bigTotal = bigTotal.add(this.cashBalance || 0);

        return this.formatCurrency(bigTotal, true);
      },
    },
    clientFundingStatus() {
      if (this.userProfile.clientId && !this.loading.fundingStatus) {
        var status = this.funding.fundingArray.find((x) => x.clientId == this.userProfile.clientId);
        return status;
      }
      return null;
    },
    accountBalance() {
      if (this.userProfile && this.userProfile.clientId) {
        return this.funding.budgets.find(
          (x) => x.clientId == this.userProfile.clientId && x.masterBudget
        );
      }
      return null;
    },
    destinationBudget() {
      // Return a budget where the user has owner privileges
      if (this.accountBalance && this.isBudgetOwner(this.accountBalance))
        return this.accountBalance;
      const budgetWithBalance = this.funding.budgets.find(
        (x) => this.isBudgetOwner(x) && x.budgetBalance
      );
      if (budgetWithBalance) return budgetWithBalance;
      return this.funding.budgets.find((x) => this.isBudgetOwner(x));
    },
    showAppClipButton() {
      if (!this.isPaymentCardEnabled()) return false;

      var ua = window.navigator.userAgent;
      var iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i);
      var webkit = !!ua.match(/WebKit/i);
      var iOSSafari = iOS && webkit && !ua.match(/CriOS/i);
      // console.log("showAppClipButton ", { ua, iOS, webkit, iOSSafari });
      if (this.userProfile && (this.userProfile.businessEmail || "").includes("@wewhistle.com"))
        return !!(
          iOS &&
          // webkit &&
          // iOSSafari &&
          this.showVirtualCardInfo &&
          process.env.VUE_APP_APP_CLIP_URL
        );

      return !!(iOSSafari && this.showVirtualCardInfo && process.env.VUE_APP_APP_CLIP_URL);
    },
    userAgent() {
      return window.navigator.userAgent;
    },
    showAndroidButton() {
      if (!this.isPaymentCardEnabled()) return false;

      var ua = window.navigator.userAgent;
      console.log(ua.indexOf("android"));
      const isAndroid = ua.indexOf("android") > -1 || ua.indexOf("Android") > -1;
      // var iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i);
      // var webkit = !!ua.match(/WebKit/i);
      // var iOSSafari = iOS && webkit && !ua.match(/CriOS/i);
      const email = (this.userProfile && this.userProfile.businessEmail) || "";
      // console.log("showAndroidButton ", { ua, isAndroid, email });
      if (email.includes("@wewhistle.com") && !email.includes("drew"))
        return !!(isAndroid && this.showVirtualCardInfo && process.env.VUE_APP_INSTANT_APP_URL);
      else if (["daniel@wewhistle.com", "ben@wewhistle.com", "phil@wewhistle.com"].includes(email))
        return true;
      return false;
    },
    appClipUrl() {
      // let url = `https://app.wewhistle.com/wallet/app-clip?token=${this
      // .magicLinkToken || this.token}`;
      // let url = `http://localhost:8080/wallet/app-clip?token=${this
      // .magicLinkToken || this.token}`;
      let url = `${process.env.VUE_APP_APP_CLIP_URL}&token=${this.magicLinkToken || this.token}`;
      if (this.magicLinkToken) url += `&magic-link=1`;
      return url;
    },
    instantAppUrl() {
      let url = `${process.env.VUE_APP_INSTANT_APP_URL}?token=${this.magicLinkToken || this.token}`;
      if (this.magicLinkToken) url += `&magic-link=1`;
      return url;
    },
    showMyFunds() {
      // Show my funds if they have any payment rules
      // If they have no payment rules, then we hide it since they have one balance
      return this.sortedPaymentRules.length > 0;
    },
    disableVirtualCardTile() {
      return (
        this.showTaxInfoGate ||
        (!this.showVirtualCardInfo && !this.showCardRequestForm && !this.isTest)
      );
    },
    // `showTaxInfoGate` is provided by the taxInfoGate mixin. It applies even
    // when a card has already been issued - those users must capture tax info
    // before they can view their card again.
    canIssueCardOnTaxInfo() {
      // Only ask the backend to issue once Marqeta would actually accept it.
      // Without a Marqeta user there is nothing to issue against - they have to
      // go through the request flow, which creates the user and captures the
      // cardholder agreements. Asking again for someone who already holds a
      // virtual card is rejected by Marqeta, and that rejection is not a real
      // error worth showing, so we simply never ask.
      return (
        this.isPaymentCardEnabled() &&
        !this.showVirtualCardInfo &&
        !!this.marqeta.alreadyRegistered &&
        !!this.marqeta.canRegister &&
        !this.marqeta.error_code
      );
    },
  },
  watch: {
    displayWalletMenu(val) {
      if (!val && !this.isModal) {
        console.log("Triggering balance and budget refresh once the wallet drawer closes");
        this.refreshWallet();
        this.getBudgets();
        // If the drawer opens AND we're the wallet page watching AND the virtual card is already visible, then we hide it
      } else if (
        val &&
        !this.isModal &&
        this.display.virtualCard &&
        // The only time we want to keep the virtual card open was for tokenization
        this.walletMenuScreen != "display-add-to-mobile"
      ) {
        this.toggleVirtualCardWidget();
      }
    },
  },
};
</script>

<style scoped>
.white-background {
  background: white;
  background-color: white;
  transform: scale(2);
}

.available {
  font-size: 20px;
}

/* Hides gray background when clicking v-card */
.v-card--link:before {
  background: none;
}

.list-slide-item {
  transition: all 0.5s;
  display: inline-block;
}
.virtual-card-slide {
  max-width: 100%;
}
.list-slide-enter,
.list-slide-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
.list-slide-leave-active {
  position: absolute;
}

.multi-card-gradient {
  border-radius: 8px;
}

.tax-info-prompt {
  background-color: var(--v-brandCyanLight-base);
}

.multi-card-gradient > .v-card {
  border-radius: 8px;
}
</style>
